import React from 'react';
import { Checkbox, Modal } from 'antd';

/**
 * Modal.confirm content with the "send notification email" choice. antd mounts
 * static Modal.confirm content into a detached React root WITHOUT the app's
 * IntlProvider/umi-locale context. It must therefore not call any context hooks
 * (e.g. useIntl) — doing so throws and the modal silently fails to mount.
 * Strings are pre-formatted by the caller and passed in as props.
 */
export const EmailDecisionContent: React.FC<{
  message?: string;
  label: string;
  defaultChecked?: boolean;
  checkboxTestId?: string;
  onChange: (value: boolean) => void;
}> = ({ message, label, defaultChecked = true, checkboxTestId, onChange }) => (
  <div>
    {message && <p>{message}</p>}
    <Checkbox
      data-testid={checkboxTestId}
      defaultChecked={defaultChecked}
      onChange={(e) => onChange(e.target.checked)}
    >
      {label}
    </Checkbox>
  </div>
);

interface ConfirmCalendarEventDeleteOptions {
  /** Pre-formatted confirmation question. */
  title: string;
  /** Pre-formatted checkbox label; rendered only when showEmailChoice. */
  emailChoiceLabel: string;
  /** Offer the notify-customer checkbox (lead-linked events only). */
  showEmailChoice: boolean;
}

/**
 * Delete confirmation popup with the notify-the-customer choice — the same
 * pattern as the drag-and-drop reschedule confirm in BigCalendarWrapper. The
 * email choice defaults to off, matching the reschedule default for calendar
 * events. Resolves `confirmed: false` when the popup is cancelled.
 */
export const confirmCalendarEventDelete = (
  options: ConfirmCalendarEventDeleteOptions,
): Promise<{ confirmed: boolean; sendEmail: boolean }> =>
  new Promise((resolve) => {
    const choice = { sendEmail: false };
    Modal.confirm({
      title: options.title,
      okButtonProps: { danger: true, 'data-testid': 'calendar-event-delete-confirm-btn' } as any,
      cancelButtonProps: { 'data-testid': 'calendar-event-delete-cancel-btn' } as any,
      content: options.showEmailChoice ? (
        <EmailDecisionContent
          label={options.emailChoiceLabel}
          defaultChecked={false}
          checkboxTestId="calendar-event-delete-send-email"
          onChange={(value) => {
            choice.sendEmail = value;
          }}
        />
      ) : undefined,
      onOk: () => resolve({ confirmed: true, sendEmail: choice.sendEmail }),
      onCancel: () => resolve({ confirmed: false, sendEmail: false }),
    });
  });

/**
 * Minimal slice of umi's intl object. Strings are formatted here, synchronously
 * at call time — the intl OBJECT works outside React context, only the useIntl
 * HOOK doesn't (see EmailDecisionContent).
 */
type RescheduleIntl = {
  formatMessage: (descriptor: { id: string; defaultMessage?: string }) => string;
};

interface ConfirmRescheduleOptions {
  intl: RescheduleIntl;
  /** Calendar event (blocker) vs service request. */
  isBlocker: boolean;
  /** Blocker linked to a lead — the backend only mails then. */
  isLeadLinked: boolean;
  /** control_emails permission: lets service-request users skip the email. */
  hasControlEmails: boolean;
}

/**
 * Drag-and-drop reschedule confirmation with the send-notification-email
 * decision:
 * - Blocker with a linked lead: user picks, default off.
 * - Blocker without a lead: nothing to notify — resolves confirmed, no popup.
 * - Service request with control_emails: user picks, default on.
 * - Other service requests: plain informational confirm, email always sent.
 * Resolves `confirmed: false` when the popup is cancelled.
 */
export const confirmReschedule = (
  options: ConfirmRescheduleOptions,
): Promise<{ confirmed: boolean; sendEmail: boolean }> =>
  new Promise((resolve) => {
    const { intl, isBlocker, isLeadLinked, hasControlEmails } = options;

    if (isBlocker && !isLeadLinked) {
      resolve({ confirmed: true, sendEmail: false });
      return;
    }

    const message = intl.formatMessage({
      id: 'pages.serviceRequest.rescheduleConfirmation',
      defaultMessage:
        'The customer will receive an invitation about the new appointment date and time',
    });

    const showEmailChoice = isBlocker || hasControlEmails;
    const defaultSendEmail = !isBlocker;
    const choice = { sendEmail: showEmailChoice ? defaultSendEmail : true };

    Modal.confirm({
      content: showEmailChoice ? (
        <EmailDecisionContent
          message={message}
          label={intl.formatMessage({
            id: 'pages.serviceRequest.rescheduleSendEmail',
            defaultMessage: 'Send notification email',
          })}
          defaultChecked={defaultSendEmail}
          checkboxTestId="reschedule-send-email"
          onChange={(value) => {
            choice.sendEmail = value;
          }}
        />
      ) : (
        message
      ),
      okText: intl.formatMessage({
        id: 'pages.serviceRequest.reschedule',
        defaultMessage: 'Reschedule',
      }),
      okButtonProps: { 'data-testid': 'reschedule-confirm-btn' } as any,
      cancelButtonProps: { 'data-testid': 'reschedule-cancel-btn' } as any,
      onOk: () => resolve({ confirmed: true, sendEmail: choice.sendEmail }),
      onCancel: () => resolve({ confirmed: false, sendEmail: false }),
    });
  });
